import { useState, useEffect } from 'react';
import { useGoogleContacts } from '@/hooks/useGoogleContacts';
import GoogleContactsModal from '@/components/onboarding/GoogleContactsModal';
import PersonalizeContactModal from '@/components/contacts/PersonalizeContactModal';
import { useCreateContact, useContactLimits } from '@/hooks/api/useContacts';
import { useToast } from '@/hooks/use-toast';
import type { GoogleContact } from '@/types/googleContacts';
import type { Contact } from '@/types/api';

interface GoogleImportFlowProps {
  onComplete: () => void;
  onCancel: () => void;
}

export function GoogleImportFlow({ onComplete, onCancel }: GoogleImportFlowProps) {
  const [showGoogleModal, setShowGoogleModal] = useState(false);
  const [showPersonalizeModal, setShowPersonalizeModal] = useState(false);
  const [importedContacts, setImportedContacts] = useState<Contact[]>([]);
  const [currentContactIndex, setCurrentContactIndex] = useState(0);

  const { contacts, isLoading, loadContacts } = useGoogleContacts();
  const createContact = useCreateContact();
  const { data: contactLimits } = useContactLimits();
  const { toast } = useToast();

  // Start the flow by loading Google contacts
  useEffect(() => {
    const startGoogleAuth = async () => {
      try {
        await loadContacts();
        setShowGoogleModal(true);
      } catch (error: any) {
        if (error?.error === 'popup_closed_by_user' || error?.error === 'access_denied') {
          toast({
            title: 'Access Cancelled',
            description: 'Google contacts import was cancelled.',
          });
          onCancel();
        } else {
          toast({
            title: 'Error',
            description: 'Failed to load Google contacts. Please try again.',
            variant: 'destructive',
          });
          onCancel();
        }
      }
    };

    startGoogleAuth();
  }, []);

  const handleGoogleImport = async (selectedContacts: GoogleContact[]) => {
    setShowGoogleModal(false);

    if (selectedContacts.length === 0) {
      onCancel();
      return;
    }

    // Check if selected contacts exceed the limit
    if (contactLimits && selectedContacts.length > contactLimits.remaining) {
      toast({
        title: 'Contact limit exceeded',
        description: `You can only add ${contactLimits.remaining} more contact${contactLimits.remaining === 1 ? '' : 's'}. Please select fewer contacts.`,
        variant: 'destructive',
      });
      onCancel();
      return;
    }

    // Create contacts in the backend
    const createdContacts: Contact[] = [];

    for (const googleContact of selectedContacts) {
      const name = googleContact.names?.[0]?.displayName || 'Unknown';
      const email = googleContact.emailAddresses?.[0]?.value;
      const phone = googleContact.phoneNumbers?.[0]?.value;

      try {
        const newContact = await createContact.mutateAsync({
          name,
          email,
          phone,
        });
        console.log('Created contact:', newContact);
        if (newContact && newContact.id) {
          createdContacts.push(newContact);
        } else {
          console.error('Contact created but missing ID:', newContact);
          toast({
            title: 'Error',
            description: `Failed to import ${name} - invalid response`,
            variant: 'destructive',
          });
        }
      } catch (error) {
        console.error('Failed to create contact:', error);
        toast({
          title: 'Error',
          description: `Failed to import ${name}`,
          variant: 'destructive',
        });
      }
    }

    if (createdContacts.length > 0) {
      setImportedContacts(createdContacts);
      setCurrentContactIndex(0);
      setShowPersonalizeModal(true);
    } else {
      toast({
        title: 'Import Failed',
        description: 'No contacts were imported.',
        variant: 'destructive',
      });
      onCancel();
    }
  };

  const handlePersonalizeSave = () => {
    // Move to next contact
    if (currentContactIndex < importedContacts.length - 1) {
      setCurrentContactIndex(currentContactIndex + 1);
    } else {
      // All contacts personalized
      setShowPersonalizeModal(false);
      toast({
        title: 'Success',
        description: `${importedContacts.length} contact(s) imported successfully!`,
      });
      onComplete();
    }
  };

  const handlePersonalizeSkip = () => {
    // Move to next contact without saving changes
    if (currentContactIndex < importedContacts.length - 1) {
      setCurrentContactIndex(currentContactIndex + 1);
    } else {
      // All contacts processed
      setShowPersonalizeModal(false);
      toast({
        title: 'Success',
        description: `${importedContacts.length} contact(s) imported!`,
      });
      onComplete();
    }
  };

  const handlePersonalizeClose = () => {
    // User wants to exit the flow
    setShowPersonalizeModal(false);

    // Show appropriate message based on progress
    if (currentContactIndex === 0) {
      toast({
        title: 'Import Complete',
        description: `${importedContacts.length} contact(s) imported. You can personalize them later from the People page.`,
      });
    } else if (currentContactIndex === importedContacts.length - 1) {
      // On the last contact
      toast({
        title: 'Import Complete',
        description: `${importedContacts.length} contact(s) imported and personalized!`,
      });
    } else {
      toast({
        title: 'Import Complete',
        description: `${importedContacts.length} contact(s) imported. ${currentContactIndex} personalized.`,
      });
    }

    onComplete();
  };

  const currentContact = importedContacts[currentContactIndex];

  // Debug log
  if (showPersonalizeModal && currentContact) {
    console.log('Current contact for personalization:', currentContact);
    console.log('Contact ID:', currentContact.id);
  }

  return (
    <>
      {showGoogleModal && (
        <GoogleContactsModal
          isOpen={showGoogleModal}
          onClose={() => {
            setShowGoogleModal(false);
            onCancel();
          }}
          contacts={contacts}
          isLoading={isLoading}
          onImport={handleGoogleImport}
          maxSelectable={contactLimits?.remaining}
        />
      )}

      {showPersonalizeModal && currentContact && currentContact.id && (
        <PersonalizeContactModal
          isOpen={showPersonalizeModal}
          onClose={handlePersonalizeClose}
          contact={currentContact}
          onSuccess={handlePersonalizeSave}
          onSkip={handlePersonalizeSkip}
          showSkipButton={true}
        />
      )}
    </>
  );
}
